import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { workflowApi, WorkflowListItem, WorkflowStats, Connector } from '../lib/workflowApi'
import {
  Play,
  Pause,
  Plus,
  Clock,
  Mail,
  Webhook,
  Rss,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2,
  Eye,
  Edit,
  Zap,
  Search,
  ChevronDown,
} from 'lucide-react'
import { useAppLanguage } from '../i18n'

// Formatters
const numberFormatter = new Intl.NumberFormat('fr-FR')
const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

// Icônes des connecteurs
const connectorIcons: Record<string, React.ReactNode> = {
  schedule: <Clock className="w-4 h-4" />,
  manual: <Play className="w-4 h-4" />,
  webhook: <Webhook className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  email_imap: <Mail className="w-4 h-4" />,
  rss_feed: <Rss className="w-4 h-4" />,
  slack: <Zap className="w-4 h-4" />,
  webhook_out: <Webhook className="w-4 h-4" />,
}

// Noms français des connecteurs
const connectorNames: Record<string, string> = {
  schedule: 'Planification',
  manual: 'Manuel',
  webhook: 'Webhook',
  email: 'Email',
  email_imap: 'Email IMAP',
  rss_feed: 'Flux RSS',
  slack: 'Slack',
  webhook_out: 'Webhook sortant',
}

type Agent = {
  id: number
  name: string
  slug: string
}

export default function WorkflowsPage() {
  const navigate = useNavigate()
  const { t, language } = useAppLanguage()

  // Formatters based on language
  const numberFormatter = new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US')
  const dateFormatter = new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  // Données
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([])
  const [stats, setStats] = useState<WorkflowStats | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [connectors, setConnectors] = useState<Connector[]>([])

  // État UI
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterAgent, setFilterAgent] = useState<number | null>(null)
  const [filterEnabled, setFilterEnabled] = useState<boolean | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WorkflowListItem | null>(null)

  // Charger les données
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [workflowsRes, statsRes, agentsRes, connectorsRes] = await Promise.all([
        workflowApi.listWorkflows({
          agent_id: filterAgent || undefined,
          is_enabled: filterEnabled ?? undefined,
        }),
        workflowApi.getStats(),
        api.get('/agents').then((r) => r.data),
        workflowApi.listConnectors(),
      ])

      setWorkflows(workflowsRes.workflows)
      setStats(statsRes)
      setAgents(agentsRes.items || [])
      setConnectors(connectorsRes)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || t.workflows.errors.load)
    } finally {
      setLoading(false)
    }
  }, [filterAgent, filterEnabled])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Activer/Désactiver un workflow
  const toggleEnabled = async (workflow: WorkflowListItem) => {
    try {
      if (workflow.is_enabled) {
        await workflowApi.disableWorkflow(workflow.id)
      } else {
        await workflowApi.enableWorkflow(workflow.id)
      }
      loadData()
    } catch (err: any) {
      setError(err?.response?.data?.detail || t.workflows.errors.toggle)
    }
  }

  // Supprimer un workflow
  const deleteWorkflow = async () => {
    if (!deleteTarget) return
    try {
      await workflowApi.deleteWorkflow(deleteTarget.id)
      setDeleteTarget(null)
      loadData()
    } catch (err: any) {
      setError(err?.response?.data?.detail || t.workflows.errors.delete)
    }
  }

  // Filtrer les workflows
  const filteredWorkflows = workflows.filter((wf) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      if (!wf.name.toLowerCase().includes(term) && !wf.description?.toLowerCase().includes(term)) {
        return false
      }
    }
    return true
  })

  // Statistiques d'aperçu
  const overviewStats = [
    {
      label: t.workflows.stats.activeWorkflows,
      value: numberFormatter.format(stats?.total_enabled || 0),
      description: t.workflows.stats.activeDescription,
    },
    {
      label: t.workflows.stats.executionsToday,
      value: numberFormatter.format(stats?.executions_today || 0),
      description: t.workflows.stats.executionsDescription,
    },
    {
      label: t.workflows.stats.successRate,
      value: stats?.success_rate != null ? `${Math.round(stats.success_rate)}%` : '—',
      description: t.workflows.stats.successDescription,
    },
    {
      label: t.workflows.stats.totalWorkflows,
      value: numberFormatter.format(stats?.total_workflows || 0),
      description: t.workflows.stats.totalDescription,
    },
  ]

  return (
    <div className="relative overflow-hidden">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 px-1 py-3 sm:px-6 sm:py-6">
        {/* Modal de suppression */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
            <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl shadow-red-300/30 dark:border-red-900/60 dark:bg-zinc-950/95">
              <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
                <div>
                  <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">{t.workflows.delete.title}</h2>
                  <p className="text-sm text-red-600/80 dark:text-red-300/80">{t.workflows.delete.irreversible}</p>
                </div>
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="rounded-full border border-red-200/70 px-3 py-1 text-sm text-red-600 transition hover:bg-red-100/60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
                >
                  {t.workflows.delete.close}
                </button>
              </div>
              <div className="space-y-4 px-6 py-6 text-sm text-zinc-700 dark:text-zinc-200">
                <p>
                  {t.workflows.delete.confirm}{' '}
                  <span className="font-semibold text-zinc-900 dark:text-white">{deleteTarget.name}</span> ?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="rounded-xl px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    {t.workflows.delete.cancel}
                  </button>
                  <button
                    onClick={deleteWorkflow}
                    className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    {t.workflows.delete.delete}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">{t.common.close}</button>
          </div>
        )}

        {/* En-tête avec bouton créer */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {t.workflows.tagline}
          </p>
          <button
            onClick={() => navigate('/workflows/new')}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-500 hover:shadow-emerald-500/40"
          >
            <Plus className="w-4 h-4" />
            {t.workflows.newWorkflow}
          </button>
        </div>

        {/* Statistiques */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {overviewStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl border border-emerald-100/80 bg-white/70 p-4 backdrop-blur-sm dark:border-emerald-900/40 dark:bg-zinc-900/70"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {stat.label}
              </p>
              <p className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">{stat.value}</p>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{stat.description}</p>
            </div>
          ))}
        </section>

        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder={t.workflows.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-zinc-200/80 bg-white/70 py-2.5 pl-10 pr-4 text-sm backdrop-blur-sm placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700/80 dark:bg-zinc-900/70 dark:text-white"
            />
          </div>

          {/* Filtre par agent */}
          <div className="relative">
            <select
              value={filterAgent || ''}
              onChange={(e) => setFilterAgent(e.target.value ? Number(e.target.value) : null)}
              className="appearance-none rounded-xl border border-zinc-200/80 bg-white/70 py-2.5 pl-4 pr-10 text-sm backdrop-blur-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700/80 dark:bg-zinc-900/70 dark:text-white"
            >
              <option value="">{t.workflows.allAgents}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          </div>

          {/* Filtre par statut */}
          <div className="relative">
            <select
              value={filterEnabled === null ? '' : filterEnabled ? 'enabled' : 'disabled'}
              onChange={(e) => {
                if (e.target.value === '') setFilterEnabled(null)
                else setFilterEnabled(e.target.value === 'enabled')
              }}
              className="appearance-none rounded-xl border border-zinc-200/80 bg-white/70 py-2.5 pl-4 pr-10 text-sm backdrop-blur-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700/80 dark:bg-zinc-900/70 dark:text-white"
            >
              <option value="">{t.workflows.allStatuses}</option>
              <option value="enabled">{t.workflows.active}</option>
              <option value="disabled">{t.workflows.inactive}</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Liste des workflows */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500/30 border-t-emerald-500" />
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-12 text-center backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
            <Zap className="mx-auto h-12 w-12 text-zinc-300 dark:text-zinc-600" />
            <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">{t.workflows.empty.title}</h3>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {searchTerm || filterAgent || filterEnabled !== null
                ? t.workflows.empty.noResults
                : t.workflows.empty.createFirst}
            </p>
            {!searchTerm && !filterAgent && filterEnabled === null && (
              <button
                onClick={() => navigate('/workflows/new')}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                <Plus className="w-4 h-4" />
                {t.workflows.createWorkflow}
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredWorkflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onToggle={() => toggleEnabled(workflow)}
                onView={() => navigate(`/workflows/${workflow.id}`)}
                onEdit={() => navigate(`/workflows/${workflow.id}/edit`)}
                onDelete={() => setDeleteTarget(workflow)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Carte de workflow
function WorkflowCard({
  workflow,
  onToggle,
  onView,
  onEdit,
  onDelete,
}: {
  workflow: WorkflowListItem
  onToggle: () => void
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t, language } = useAppLanguage()
  
  // Get connector name from translations
  const getConnectorName = (slug: string | null) => {
    if (!slug) return t.workflows.connectors.unknown
    return t.workflows.connectors[slug as keyof typeof t.workflows.connectors] || slug
  }
  
  const triggerIcon = (workflow.trigger_connector_slug && connectorIcons[workflow.trigger_connector_slug]) || <Zap className="w-4 h-4" />
  const actionIcon = (workflow.action_connector_slug && connectorIcons[workflow.action_connector_slug]) || <Zap className="w-4 h-4" />
  const triggerName = getConnectorName(workflow.trigger_connector_slug)
  const actionName = getConnectorName(workflow.action_connector_slug)
  
  // Date formatter based on language
  const dateFormatter = new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  return (
    <div className="group relative rounded-2xl border border-zinc-200/80 bg-white/70 p-5 backdrop-blur-sm transition hover:border-emerald-300/80 hover:shadow-lg hover:shadow-emerald-500/10 dark:border-zinc-700/80 dark:bg-zinc-900/70 dark:hover:border-emerald-700/80">
      {/* En-tête */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-zinc-900 dark:text-white truncate">{workflow.name}</h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
            {t.workflows.card.agent}: {(workflow as any).agent_name || `#${workflow.agent_id}`}
          </p>
        </div>
        <button
          onClick={onToggle}
          className={`shrink-0 rounded-full p-1.5 transition ${
            workflow.is_enabled
              ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-400'
              : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-500'
          }`}
          title={workflow.is_enabled ? t.workflows.card.disable : t.workflows.card.enable}
        >
          {workflow.is_enabled ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        </button>
      </div>

      {/* Description */}
      {workflow.description && (
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 line-clamp-2">{workflow.description}</p>
      )}

      {/* Connecteurs */}
      <div className="mt-4 flex items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-1 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          {triggerIcon}
          {triggerName}
        </span>
        <span className="text-zinc-300 dark:text-zinc-600">→</span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 px-2 py-1 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
          {actionIcon}
          {actionName}
        </span>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <p className="font-semibold text-zinc-900 dark:text-white">{workflow.run_count}</p>
          <p className="text-zinc-500 dark:text-zinc-400">{t.workflows.card.executions}</p>
        </div>
        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <p className="font-semibold text-emerald-600 dark:text-emerald-400">{workflow.success_count}</p>
          <p className="text-zinc-500 dark:text-zinc-400">{t.workflows.card.success}</p>
        </div>
        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <p className="font-semibold text-red-600 dark:text-red-400">{workflow.failure_count}</p>
          <p className="text-zinc-500 dark:text-zinc-400">{t.workflows.card.failures}</p>
        </div>
      </div>

      {/* Prochaine exécution */}
      {workflow.next_run_at && (
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          {t.workflows.card.nextRun}: {dateFormatter.format(new Date(workflow.next_run_at))}
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2 border-t border-zinc-100 pt-4 dark:border-zinc-800">
        <button
          onClick={onView}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <Eye className="w-3.5 h-3.5" />
          {t.workflows.card.view}
        </button>
        <button
          onClick={onEdit}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
        >
          <Edit className="w-3.5 h-3.5" />
          {t.workflows.card.edit}
        </button>
        <button
          onClick={onDelete}
          className="inline-flex items-center justify-center rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
          title={t.workflows.card.delete}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
